import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Collapse,
  Flex,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AlertIcon from '../../../assets/icons/warning-2-icon.svg';
import { getPracticeClosures } from '../../../services/clousures/practiceClosureService';
import { FormattedOfficeHour, OfficeHour } from '../../../types';
import { PracticeClosure } from '../../../types/closures/types';
import {
  daysOfWeek,
  formatOfficeHours,
} from '../../../utils/formatOfficeHours';
import {
  normalizeTimeString,
  to12h,
} from '../../../utils/timeConversion';
import { timeZoneMapping } from '../../../utils/timeZoneMapping';
import { formatTimeWithCorrectTimezone } from '../../../utils/timezoneDisplay';

// Constants for UI timing (not business logic)
const MIN_NOTIFICATION_MINUTES = 30; // When to show "closing soon" notifications

// Helper to get tooltip text for each status
const getStatusTooltip = (status: string, clinicClosureEnd?: string, isMultiDay?: boolean, isPartialDay?: boolean): string => {
  switch (status) {
    case 'OPEN':
      return 'The practice is currently open and accepting patients. All services are available.';
    case 'OPENING SOON':
      return 'The practice will open within 30 minutes. Patients can prepare for their visit.';
    case 'CLOSING SOON':
      return 'The practice will close within 30 minutes. Please arrive soon if you need assistance.';
    case 'LOBBY CLOSURE APPROACHING':
      return 'The lobby will close for administrative purposes within 30 minutes. The practice remains open for scheduled patients.';
    case 'LOBBY CLOSURE':
      return 'The lobby is currently closed for administrative purposes. The practice remains open for scheduled patients only.';
    case 'CLINIC CLOSURE':
      if (clinicClosureEnd) {
        if (isMultiDay) {
          return `The clinic is closed through ${clinicClosureEnd}. All regular services are suspended during this period.`;
        }
        if (isPartialDay) {
          return `The clinic is closed until ${clinicClosureEnd}. Please check back after this time.`;
        }
        return 'The clinic is closed today. Please check back during regular office hours.';
      }
      return 'The clinic is currently closed. Please check back during regular office hours.';
    case 'CLOSED':
      return 'The practice is currently closed. Please check back during regular office hours.';
    case 'Unknown':
      return 'Unable to determine practice status. Please contact the practice directly for current hours.';
    default:
      return 'Practice status information is not available.';
  }
};


interface PracticeStatusBoxProps {
  timezone: string;
  width?: string | Record<string, string>;
  isLoading?: boolean;
  unFormattedOfficeHours?: OfficeHour[];
  id?: string;
  practiceId?: string; // Added for fetching clinic closures
  hideMoreDetails?: boolean;
  state?: string | '';
  /** When true, expands the details section by default on initial render */
  defaultOpen?: boolean;
  /** When provided (including null sentinel), skips internal getPracticeClosures fetch */
  clinicClosures?: PracticeClosure[] | null;
  isClosuresLoading?: boolean;
}

const formatTime = (time: string): string => {
  if (!time) return '';

  // If already in 12-hour format (contains AM/PM), return as is
  if (time.includes('AM') || time.includes('PM')) {
    return time;
  }

  // If in 24-hour format, convert to 12-hour
  if (/^\d{2}:\d{2}$/.test(time)) {
    return to12h(time);
  }

  // Fallback: return original time
  return time;
};

/** Ensure space before AM/PM (e.g. "8:00AM" -> "8:00 AM") */
const ensureAmPmSpace = (t: string): string =>
  String(t).trim().replace(/\s*(AM|PM)$/i, ' $1').trim();

const parseTime = (time: string, reference: Date) => {
  if (!time) return new Date(reference);

  let hour24: number;
  let minute: number;

  // Handle 12-hour format (e.g., "2:30 PM")
  if (time.includes('AM') || time.includes('PM')) {
    const [timePart, meridiem] = time
      .toUpperCase()
      .split(/(?=AM|PM)/);
    const [h, m] = timePart.split(':').map(Number);

    if (isNaN(h) || isNaN(m)) return new Date(reference);

    hour24 = h;
    if (meridiem === 'PM' && h !== 12) {
      hour24 = h + 12;
    } else if (meridiem === 'AM' && h === 12) {
      hour24 = 0;
    }
    minute = m;
  } else {
    // Handle 24-hour format (e.g., "14:30")
    const [h, m] = time.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return new Date(reference);
    hour24 = h;
    minute = m;
  }

  const date = new Date(reference);
  date.setHours(hour24, minute, 0, 0);
  return date;
};

// Helper function to format date consistently without timezone-shifting bugs
const formatDateConcise = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);

  // Format as "MMM D, YYYY" manually to avoid any timezone-based date shifting
  // This ensures that Jan 5 stays Jan 5 regardless of local browser timezone
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[month - 1]} ${day}, ${year}`;
};

/**
 * The clinic's own calendar day, not the browser's and not UTC.
 *
 * A closure is stored as plain YYYY-MM-DD dates with no offset, so deciding whether
 * one is active today is a question about the date on the wall in the clinic. Reading
 * that off the browser clock (or off toISOString(), which is UTC) puts a US practice
 * a day ahead every evening: from ~4-7pm local onward "today" resolves to tomorrow,
 * a same-day closure falls outside its own window and stops rendering, while a
 * multi-day one still covers tomorrow and keeps working. That asymmetry is the
 * reported bug, so both the status badge and the per-day hours table read the date
 * from here.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const getPracticeCalendarDay = (
  timezone: string,
  state?: string,
  nowOverride?: Date
): { dateStr: string; dayIndex: number; localNow: Date } => {
  // Arizona keeps its own zone year-round; same override the rest of the file uses.
  const ianaTimeZone =
    state === 'AZ' ? 'America/Phoenix' : timeZoneMapping[timezone] || 'UTC';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(nowOverride ?? new Date());

  const part = (type: string) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '00';

  const dateStr = `${part('year')}-${part('month')}-${part('day')}`;
  // Parsed without a zone, so this reads as the clinic's wall clock.
  const localNow = new Date(
    `${dateStr}T${part('hour')}:${part('minute')}:${part('second')}`
  );

  return { dateStr, dayIndex: localNow.getDay(), localNow };
};

/**
 * Shifts a YYYY-MM-DD date by whole days. UTC arithmetic on a date-only value, so no
 * offset or DST transition can nudge the result onto the wrong day.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const shiftIsoDate = (isoDate: string, days: number): string => {
  const [year, month, day] = isoDate.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day));
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
};

// eslint-disable-next-line react-refresh/only-export-components
export const getPracticeStatusWithLobbyClosure = (
  timezone: string,
  rawOfficeHours: OfficeHour[],
  nowOverride?: Date,
  state?: string,
  clinicClosures?: PracticeClosure[]
): {
  status: string;
  lobbyClosureStart?: string;
  lobbyClosureEnd?: string;
  dayOpenTime?: string;
  dayCloseTime?: string;
  clinicClosureEnd?: string; // Formatted end date + time for clinic closure
  isMultiDayClosure?: boolean; // Flag for multi-day vs single-day/partial
  isPartialDayClosure?: boolean; // Single-day closure with an end time (not full-day)
} => {
  const {
    dateStr: todayDateStr,
    dayIndex: todayIndex, // 0 = Sunday
    localNow: currentDate,
  } = getPracticeCalendarDay(timezone, state, nowOverride);

  // Check for active clinic closures first (highest priority)
  if (clinicClosures && clinicClosures.length > 0) {
    // Find all active closures for today and pick the one that ends LATEST
    const activeClosures = clinicClosures.filter((closure) => {
      const startDate = closure.startDate;
      const endDate = closure.endDate || closure.startDate;

      // Check if today falls within the closure period
      if (todayDateStr < startDate) return false;
      if (endDate && todayDateStr > endDate) return false;

      // If closure has specific times, check if current time is within closure window
      if (closure.startTime && closure.endTime) {
        const startTimeNorm = normalizeTimeString(closure.startTime);
        const endTimeNorm = normalizeTimeString(
          closure.endTime,
          true
        );

        if (startTimeNorm && endTimeNorm) {
          const isSingleDay =
            !closure.endDate || closure.endDate === closure.startDate;

          if (isSingleDay) {
            const closureStart = parseTime(startTimeNorm, currentDate);
            const closureEnd = parseTime(endTimeNorm, currentDate);
            if (closureEnd <= closureStart) {
              closureEnd.setDate(closureEnd.getDate() + 1);
            }
            return currentDate >= closureStart && currentDate < closureEnd;
          }

          // Multi-day: startTime gates entry on the first day, endTime gates
          // exit on the last; every day between them is active in full.
          if (todayDateStr === closure.startDate) {
            return currentDate >= parseTime(startTimeNorm, currentDate);
          }
          if (todayDateStr === closure.endDate) {
            return currentDate < parseTime(endTimeNorm, currentDate);
          }
          return true;
        }
      } else if (
        closure.endTime &&
        (!closure.endDate || closure.endDate === closure.startDate)
      ) {
        // Single-day closure with only an end time: active from open until endTime
        const endTimeNorm = normalizeTimeString(closure.endTime, true);
        if (endTimeNorm) {
          const closureEnd = parseTime(endTimeNorm, currentDate);
          if (currentDate < closureEnd) return true;
          return false;
        }
      } else if (
        closure.endTime &&
        closure.endDate &&
        closure.endDate !== closure.startDate &&
        todayDateStr === closure.endDate
      ) {
        // Multi-day closure with only an end time, on the last day: deactivate after endTime
        const endTimeNorm = normalizeTimeString(closure.endTime, true);
        if (endTimeNorm) {
          const closureEnd = parseTime(endTimeNorm, currentDate);
          if (currentDate < closureEnd) return true;
          return false;
        }
      }

      return true;
    });

    if (activeClosures.length > 0) {
      // Pick the closure that ends furthest in the future
      // Sort by endDate (or startDate if no endDate), then by endTime if dates are equal
      const activeClosure = [...activeClosures].sort((a, b) => {
        const aEndDate = a.endDate || a.startDate;
        const bEndDate = b.endDate || b.startDate;

        // First compare by end date
        const dateCompare = bEndDate.localeCompare(aEndDate);
        if (dateCompare !== 0) return dateCompare;

        // If dates are equal, compare by end time (later time = later closure)
        const aEndTime = a.endTime || '';
        const bEndTime = b.endTime || '';
        return bEndTime.localeCompare(aEndTime);
      })[0];

      // Format the end date and time for display
      let formattedEnd = '';
      let isMultiDay = false;
      let isPartialDay = false;

      if (
        activeClosure.endDate &&
        activeClosure.endDate !== activeClosure.startDate
      ) {
        // Multi-day closure: use "through" logic
        isMultiDay = true;
        const endDateFormatted = formatDateConcise(
          activeClosure.endDate
        );

        if (activeClosure.endTime) {
          const endTimeFormatted = formatTime(activeClosure.endTime);
          formattedEnd = `${endDateFormatted} at ${endTimeFormatted}`;
        } else {
          formattedEnd = endDateFormatted;
        }
      } else if (activeClosure.endTime) {
        isPartialDay = true;
        const endTimeFormatted = formatTime(activeClosure.endTime);
        formattedEnd = endTimeFormatted;
      } else {
        formattedEnd = formatDateConcise(activeClosure.startDate);
      }

      return {
        status: 'CLINIC CLOSURE',
        clinicClosureEnd: formattedEnd,
        isMultiDayClosure: isMultiDay,
        isPartialDayClosure: isPartialDay,
      };
    }
  }

  // Filter today's blocks (backend: 1=Sunday, 2=Monday, ..., 7=Saturday)
  const todayBlocks = rawOfficeHours
    .filter(
      (item) =>
        item.day_of_week === todayIndex + 1 &&
        !item.closed &&
        item.open_time &&
        item.close_time
    )
    .sort((a, b) => {
      const aOpen = normalizeTimeString(a.open_time) ?? '';
      const bOpen = normalizeTimeString(b.open_time) ?? '';
      return aOpen.localeCompare(bOpen);
    });

  // Defensive: skip if no valid blocks
  if (!todayBlocks.length) {
    return { status: 'CLOSED' };
  }

  // Check each block for open/closing soon
  for (const block of todayBlocks) {
    const openNorm = normalizeTimeString(block.open_time);
    const closeNorm = normalizeTimeString(block.close_time, true);
    if (!openNorm || !closeNorm) continue;
    const start = parseTime(openNorm, currentDate);
    const end = parseTime(closeNorm, currentDate);

    // If end is before start (shouldn't happen with normalization, but just in case), treat as next day
    if (end <= start) end.setDate(end.getDate() + 1);

    if (currentDate >= start && currentDate < end) {
      const minutesLeft = Math.floor(
        (end.getTime() - currentDate.getTime()) / 60000
      );
      if (minutesLeft <= MIN_NOTIFICATION_MINUTES) {
        // If this is the morning block of a split day, prefer LOBBY CLOSURE APPROACHING over CLOSING SOON
        if (todayBlocks.length === 2 && block === todayBlocks[0]) {
          return {
            status: 'LOBBY CLOSURE APPROACHING',
            lobbyClosureStart: todayBlocks[0].close_time ?? undefined,
            lobbyClosureEnd: todayBlocks[1].open_time ?? undefined,
          };
        }
        return {
          status: 'CLOSING SOON',
          dayCloseTime:
            todayBlocks[todayBlocks.length - 1].close_time ??
            undefined,
        };
      }
      return { status: 'OPEN' };
    }
  }

  // If not currently open in any block, check if opening soon (first block of the day)
  if (todayBlocks.length >= 1) {
    const firstBlockOpenNorm = normalizeTimeString(
      todayBlocks[0].open_time
    );
    if (firstBlockOpenNorm) {
      const firstBlockOpen = parseTime(
        firstBlockOpenNorm,
        currentDate
      );
      if (currentDate < firstBlockOpen) {
        const minutesUntilOpen = Math.floor(
          (firstBlockOpen.getTime() - currentDate.getTime()) / 60000
        );
        if (minutesUntilOpen <= MIN_NOTIFICATION_MINUTES) {
          return {
            status: 'OPENING SOON',
            dayOpenTime: todayBlocks[0].open_time ?? undefined,
          };
        }
      }
    }
  }

  // If two blocks, check for lobby closure gap
  if (todayBlocks.length === 2) {
    const endOfMorningNorm = normalizeTimeString(
      todayBlocks[0].close_time,
      true
    );
    const startOfAfternoonNorm = normalizeTimeString(
      todayBlocks[1].open_time
    );

    if (endOfMorningNorm && startOfAfternoonNorm) {
      const endOfMorning = parseTime(endOfMorningNorm, currentDate);
      const startOfAfternoon = parseTime(
        startOfAfternoonNorm,
        currentDate
      );

      // Check if currently during lobby closure
      if (
        currentDate >= endOfMorning &&
        currentDate < startOfAfternoon
      ) {
        return {
          status: 'LOBBY CLOSURE',
          lobbyClosureStart: todayBlocks[0].close_time ?? undefined,
          lobbyClosureEnd: todayBlocks[1].open_time ?? undefined,
        };
      }

      // Check if lobby closure is approaching (within 30 minutes)
      if (currentDate < endOfMorning) {
        const minutesUntilLobbyClosure = Math.floor(
          (endOfMorning.getTime() - currentDate.getTime()) / 60000
        );

        if (minutesUntilLobbyClosure <= MIN_NOTIFICATION_MINUTES) {
          return {
            status: 'LOBBY CLOSURE APPROACHING',
            lobbyClosureStart: todayBlocks[0].close_time ?? undefined,
            lobbyClosureEnd: todayBlocks[1].open_time ?? undefined,
          };
        }
      }

      // Opening soon handled above for all cases (including two-block days)
    }
  }

  // Otherwise, closed
  return { status: 'CLOSED' };
};

const PracticeStatusBox: React.FC<PracticeStatusBoxProps> = ({
  timezone,
  width,
  isLoading,
  unFormattedOfficeHours,
  hideMoreDetails = false,
  state,
  practiceId,
  defaultOpen = false,
  clinicClosures,
  isClosuresLoading,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [status, setStatus] = useState('OPEN');
  const [badgeColor, setBadgeColor] = useState('green');
  const [clinicClosureEnd, setClinicClosureEnd] = useState<
    string | undefined
  >(undefined);
  const [isMultiDayClosure, setIsMultiDayClosure] =
    useState<boolean>(false);
  const [isPartialDayClosure, setIsPartialDayClosure] =
    useState<boolean>(false);
  const [lobbyClosureInfo, setLobbyClosureInfo] = useState<{
    start: string;
    end: string;
    dayOpenTime?: string;
    dayCloseTime?: string;
  } | null>(null);
  const [officeHours, setOfficeHours] = useState<
    FormattedOfficeHour[] | []
  >([]);
  const [internalClinicClosures, setInternalClinicClosures] = useState<
    PracticeClosure[]
  >([]);
  const [formattedDateTime, setFormattedDateTime] = useState(() => {
    const resolvedTimeZone =
      state === 'AZ'
        ? 'America/Phoenix'
        : timeZoneMapping[timezone] || 'UTC';

    const timeInfo = formatTimeWithCorrectTimezone(
      timezone,
      resolvedTimeZone,
      {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }
    );

    return timeInfo.timeString;
  });

  useEffect(() => {
    const updateTime = () => {
      try {
        if (!timezone) {
          setFormattedDateTime('Unknown Timezone');
          return;
        }

        const resolvedTimeZone =
          state === 'AZ'
            ? 'America/Phoenix'
            : timeZoneMapping[timezone] || 'UTC';

        const timeInfo = formatTimeWithCorrectTimezone(
          timezone,
          resolvedTimeZone,
          {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }
        );

        setFormattedDateTime(timeInfo.timeString);
      } catch (error) {
        console.error(
          `Error formatting date for timezone "${timezone}":`,
          error
        );

        // Fallback to UTC
        const now = new Date();
        const fallbackTime = now.toLocaleString('en-US', {
          timeZone: 'UTC',
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });

        setFormattedDateTime(
          `Invalid timezone: ${timezone}. Showing UTC time: ${fallbackTime}`
        );
      }
    };

    updateTime(); // Initial call to set the time

    const intervalId = setInterval(updateTime, 60000); // Update time every 60 seconds
    return () => clearInterval(intervalId); // Cleanup on component unmount
  }, [state, timezone]);

  const [datePart, timePart] = formattedDateTime.split(', ').reduce(
    (acc, curr, index) => {
      if (index === 2) {
        acc[1] += `, ${curr}`;
      } else {
        acc[0] += `${index > 0 ? ', ' : ''}${curr}`;
      }
      return acc;
    },
    ['', '']
  );

  // Fetch clinic closures when practiceId is available
  const fetchClinicClosures = useCallback(async () => {
    if (!practiceId) {
      setInternalClinicClosures([]);
      return;
    }

    try {
      const closures = await getPracticeClosures({
        practiceIds: [practiceId],
      });
      setInternalClinicClosures(closures);
    } catch (error) {
      console.error('Failed to fetch clinic closures:', error);
      setInternalClinicClosures([]);
    }
  }, [practiceId]);

  useEffect(() => {
    if (clinicClosures !== undefined) return; // prop provided — skip internal fetch
    fetchClinicClosures();
  }, [clinicClosures, fetchClinicClosures]);

  // Compute effective closures: prefer prop over internal state
  // null means parent is still loading — treat as [] to avoid stale rendering
  const effectiveClosures = useMemo(
    () => (clinicClosures !== undefined ? (clinicClosures ?? []) : internalClinicClosures),
    [clinicClosures, internalClinicClosures]
  );

  // When closures are passed as props, use the caller's loading state; otherwise use internal loading state
  const effectiveClosuresLoading = clinicClosures !== undefined ? (isClosuresLoading ?? false) : false;

  // Listen for clinic closure update events to refresh immediately after save/delete.
  // When clinicClosures is passed as a prop, the parent (useBatchedClosures) already owns this
  // listener and handles the batch re-fetch — skip setting up a duplicate per-practice listener.
  useEffect(() => {
    if (clinicClosures !== undefined) return; // parent hook owns refresh via useBatchedClosures
    const handleClinicClosureUpdate = (
      event: CustomEvent<{ practiceId: string }>
    ) => {
      if (event.detail.practiceId === practiceId) {
        fetchClinicClosures();
      }
    };

    window.addEventListener(
      'clinicClosureUpdated',
      handleClinicClosureUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        'clinicClosureUpdated',
        handleClinicClosureUpdate as EventListener
      );
    };
  }, [clinicClosures, practiceId, fetchClinicClosures]);

  useEffect(() => {
    if (
      Array.isArray(unFormattedOfficeHours) &&
      unFormattedOfficeHours.length > 0
    ) {
      const formattedOfficeHours = formatOfficeHours({
        office_hours: unFormattedOfficeHours,
      });
      setOfficeHours(formattedOfficeHours);

      const statusInfo = getPracticeStatusWithLobbyClosure(
        timezone,
        unFormattedOfficeHours,
        undefined,
        state,
        effectiveClosures
      );

      setStatus(statusInfo.status);
      setClinicClosureEnd(statusInfo.clinicClosureEnd);
      setIsMultiDayClosure(!!statusInfo.isMultiDayClosure);
      setIsPartialDayClosure(!!statusInfo.isPartialDayClosure);

      switch (statusInfo.status) {
        case 'OPEN':
          setBadgeColor('green');
          break;
        case 'OPENING SOON':
          setBadgeColor('blue');
          break;
        case 'CLOSING SOON':
          setBadgeColor('orange');
          break;
        case 'LOBBY CLOSURE APPROACHING':
          setBadgeColor('orange');
          break;
        case 'LOBBY CLOSURE':
          setBadgeColor('yellow');
          break;
        case 'CLINIC CLOSURE':
          setBadgeColor('red');
          break;
        case 'CLOSED':
          setBadgeColor('red');
          break;
        case 'Unknown':
          setBadgeColor('gray');
          break;
        default:
          setBadgeColor('red');
      }

      if (
        statusInfo.lobbyClosureStart &&
        statusInfo.lobbyClosureEnd
      ) {
        setLobbyClosureInfo({
          start: formatTime(statusInfo.lobbyClosureStart),
          end: formatTime(statusInfo.lobbyClosureEnd),
          dayOpenTime: statusInfo.dayOpenTime
            ? formatTime(statusInfo.dayOpenTime)
            : undefined,
          dayCloseTime: statusInfo.dayCloseTime
            ? formatTime(statusInfo.dayCloseTime)
            : undefined,
        });
      } else if (statusInfo.dayOpenTime || statusInfo.dayCloseTime) {
        setLobbyClosureInfo({
          start: '',
          end: '',
          dayOpenTime: statusInfo.dayOpenTime
            ? formatTime(statusInfo.dayOpenTime)
            : undefined,
          dayCloseTime: statusInfo.dayCloseTime
            ? formatTime(statusInfo.dayCloseTime)
            : undefined,
        });
      } else {
        setLobbyClosureInfo(null);
      }
    } else if (
      Array.isArray(unFormattedOfficeHours) &&
      unFormattedOfficeHours.length === 0
    ) {
      // Only clear if we explicitly get an empty array, not if the prop is undefined/null
      // But check for clinic closures first
      if (effectiveClosures.length > 0) {
        const statusInfo = getPracticeStatusWithLobbyClosure(
          timezone,
          [],
          undefined,
          state,
          effectiveClosures
        );
        setStatus(statusInfo.status);
        setClinicClosureEnd(statusInfo.clinicClosureEnd);
        setIsMultiDayClosure(!!statusInfo.isMultiDayClosure);
        setIsPartialDayClosure(!!statusInfo.isPartialDayClosure);
        setBadgeColor(
          statusInfo.status === 'CLINIC CLOSURE' ? 'red' : 'gray'
        );
      } else {
        setOfficeHours([]);
        setStatus('Unknown');
        setBadgeColor('gray');
        setClinicClosureEnd(undefined); // Clear stale closure end date
        setIsMultiDayClosure(false);
        setIsPartialDayClosure(false);
      }
    } else if (
      !unFormattedOfficeHours ||
      unFormattedOfficeHours === undefined
    ) {
      // No office hours data available, but check for clinic closures
      if (effectiveClosures.length > 0) {
        const statusInfo = getPracticeStatusWithLobbyClosure(
          timezone,
          [],
          undefined,
          state,
          effectiveClosures
        );
        setStatus(statusInfo.status);
        setClinicClosureEnd(statusInfo.clinicClosureEnd);
        setIsMultiDayClosure(!!statusInfo.isMultiDayClosure);
        setIsPartialDayClosure(!!statusInfo.isPartialDayClosure);
        setBadgeColor(
          statusInfo.status === 'CLINIC CLOSURE' ? 'red' : 'gray'
        );
      } else {
        setOfficeHours([]);
        setStatus('Unknown');
        setBadgeColor('gray');
        setClinicClosureEnd(undefined); // Clear stale closure end date
        setIsMultiDayClosure(false);
        setIsPartialDayClosure(false);
      }
    }
    // Don't clear office hours if unFormattedOfficeHours is undefined/null
  }, [unFormattedOfficeHours, timezone, state, effectiveClosures, formattedDateTime]);

  // Deliberately not memoized. The clock above this table already re-renders the
  // component every 60s, so plain recomputation is what keeps the rows correct
  // across midnight; memoizing on props alone froze the date at mount and left a
  // tab open overnight highlighting yesterday while the clock beside it read
  // today. One Intl format per render, against an interval that builds one a
  // minute anyway.
  const practiceToday = getPracticeCalendarDay(timezone, state);

  // Same source as the closure rows, so the day highlighted as today and the day
  // marked closed can never be different rows.
  const currentDay = daysOfWeek[practiceToday.dayIndex];

  const orderedDays = daysOfWeek;

  const hasAnyOfficeHours =
    officeHours && officeHours.length > 0
      ? officeHours.some(
          (oh) => !!oh.hours && oh.hours.trim() !== ''
        )
      : false;

  return (
    <Skeleton isLoaded={!isLoading && !effectiveClosuresLoading} maxWidth={width} width={width}>
      <Box
        p="0.75rem 1rem"
        backgroundColor="gray.100"
        borderRadius="0.5rem"
        width={width}
        maxWidth="100%"
        overflow="hidden"
      >
        <Flex gap="0.75rem" justify="space-between">
          <Flex
            align="center"
            wrap="wrap"
            gap="0.75rem"
            flexWrap="wrap"
          >
            {status && timezone && (
              <Tooltip
                label={getStatusTooltip(
                  status,
                  clinicClosureEnd,
                  isMultiDayClosure,
                  isPartialDayClosure
                )}
                placement="top"
                hasArrow
                bg="gray.800"
                color="white"
                fontSize="sm"
                px={3}
                py={2}
                borderRadius="md"
                maxW="300px"
              >
                <Badge
                  variant="solid"
                  colorScheme={badgeColor}
                  cursor="help"
                >
                  {status === 'LOBBY CLOSURE'
                    ? 'Lobby Closed'
                    : status === 'LOBBY CLOSURE APPROACHING'
                    ? 'Lobby Closing'
                    : status === 'OPENING SOON'
                    ? 'Opening Soon'
                    : status === 'CLOSING SOON'
                    ? 'Closing Soon'
                    : status === 'CLINIC CLOSURE'
                    ? clinicClosureEnd
                      ? isMultiDayClosure
                        ? `CLINIC CLOSED through ${clinicClosureEnd}`
                        : isPartialDayClosure
                        ? `CLINIC CLOSED till ${clinicClosureEnd}`
                        : `CLINIC CLOSED ${clinicClosureEnd}`
                      : 'CLINIC CLOSED'
                    : status}
                </Badge>
              </Tooltip>
            )}
            <Flex gap="0.25rem" alignItems={'center'}>
              <Text
                color="gray.700"
                fontSize="0.875rem"
                fontWeight="400"
                lineHeight="1rem"
              >
                {datePart}
                {timePart && (
                  <>
                    <Text as="span" fontWeight="700">
                      {timePart}
                    </Text>
                  </>
                )}
              </Text>
            </Flex>
            <Badge variant="outline">
              {state === 'AZ'
                ? 'MST_AZ'
                : (timezone === 'MT' || timezone === 'MST' || timezone === 'MDT')
                  ? 'MT'
                  : timezone}
            </Badge>

            {status === 'OPENING SOON' &&
              lobbyClosureInfo &&
              lobbyClosureInfo.dayOpenTime && (
                <Text
                  color="blue.600"
                  fontWeight="500"
                  fontSize="0.875rem"
                  fontStyle={'italic'}
                >
                  Opening Soon at {lobbyClosureInfo.dayOpenTime}
                </Text>
              )}
            {status === 'CLOSING SOON' &&
              lobbyClosureInfo &&
              lobbyClosureInfo.dayCloseTime && (
                <Text
                  color="orange.600"
                  fontWeight="500"
                  fontSize="0.875rem"
                  fontStyle={'italic'}
                >
                  Closing Soon at {lobbyClosureInfo.dayCloseTime}
                </Text>
              )}
            {status === 'LOBBY CLOSURE APPROACHING' &&
              lobbyClosureInfo && (
                <Text
                  color="orange.600"
                  fontWeight="500"
                  fontSize="0.875rem"
                  fontStyle={'italic'}
                >
                  Lobby Closing at {lobbyClosureInfo.start}
                </Text>
              )}
            {status === 'LOBBY CLOSURE' && lobbyClosureInfo && (
              <Text
                color="orange.600"
                fontWeight="500"
                fontSize="0.875rem"
                fontStyle={'italic'}
              >
                Lobby Closed, will reopen at {lobbyClosureInfo.end}
              </Text>
            )}
          </Flex>
          {!hideMoreDetails && (
            <>
              <Flex
                gap="0.38rem"
                justify="space-between"
                align="center"
                cursor="pointer"
                onClick={() => {
                  setIsOpen(!isOpen);
                }}
              >
                {!isOpen ? (
                  <ChevronDownIcon color="text.brandLight" />
                ) : (
                  <ChevronUpIcon color="text.brandLight" />
                )}
              </Flex>
            </>
          )}
        </Flex>
        <Collapse in={isOpen} animateOpacity>
          <Stack mt="0.75rem">
            <Box>
              <Table
                variant="simple"
                size="sm"
                width="100%"
                sx={{
                  tableLayout: 'auto',
                  '& th, & td': {
                    px: '0.5rem',
                    py: '0.5rem',
                  },
                }}
              >
                <Thead>
                  <Tr>
                    <Th
                      fontSize="0.75rem"
                      fontWeight="700"
                      color="text.primary"
                      width="16%"
                      whiteSpace="nowrap"
                    >
                      Day
                    </Th>
                    <Th
                      fontSize="0.75rem"
                      fontWeight="700"
                      color="text.primary"
                      width="84%"
                      whiteSpace="normal"
                      pr="1rem"
                    >
                      Office Hours
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orderedDays.map((dayName, index) => {
                    const officeEntry =
                      officeHours?.find((oh) => oh.day === dayName) ||
                      null;
                    const isCurrentDay = dayName === currentDay;

                    // Which calendar date this weekday row stands for, walked
                    // out from the clinic's own today rather than the browser's.
                    const targetDateStr = shiftIsoDate(
                      practiceToday.dateStr,
                      daysOfWeek.indexOf(dayName) - practiceToday.dayIndex
                    );

                    const isDayClosedByClosure = effectiveClosures.some(
                      (closure) => {
                        const startDate = closure.startDate;
                        const endDate =
                          closure.endDate || closure.startDate;

                        return (
                          targetDateStr >= startDate &&
                          targetDateStr <= endDate
                        );
                      }
                    );

                    let hours: string = '';
                    if (isDayClosedByClosure) {
                      hours = 'Closed (Holiday/Event)';
                    } else if (officeEntry && officeEntry.hours) {
                      hours = officeEntry.hours;
                    } else if (hasAnyOfficeHours) {
                      hours = 'Closed';
                    } else {
                      hours = '';
                    }

                    return (
                      <Tr key={`${dayName}-${index}`}>
                        <Td
                          fontSize="0.8125rem"
                          fontWeight={isCurrentDay ? '700' : '600'}
                          color="text.primary"
                          whiteSpace="nowrap"
                          verticalAlign="top"
                        >
                          {dayName}
                        </Td>
                        <Td
                          fontSize="0.8125rem"
                          color="text.secondary"
                          verticalAlign="top"
                          wordBreak="break-word"
                          whiteSpace="normal"
                          pr="1rem"
                        >
                          {!hours ? (
                            <Text
                              color="text.secondary"
                              fontSize="0.8125rem"
                              fontWeight={
                                isCurrentDay ? '700' : '400'
                              }
                              lineHeight="1.35rem"
                            >
                              {hasAnyOfficeHours
                                ? 'Closed'
                                : '—'}
                            </Text>
                          ) : hours.toLowerCase() === 'closed' ? (
                            <Text
                              color="text.secondary"
                              fontSize="0.8125rem"
                              fontWeight={
                                isCurrentDay ? '700' : '400'
                              }
                              lineHeight="1.35rem"
                            >
                              Closed
                            </Text>
                          ) : (
                            <Stack spacing="0.35rem">
                              {hours
                                .split(',')
                                .map((segment) => segment.trim())
                                .filter(Boolean)
                                .map((segment, segmentIndex) => {
                                  const closureMatch =
                                    segment.match(
                                      /^Lobby Closure\s+(.*)\s*-\s*(.*)$/i
                                    );

                                  if (closureMatch) {
                                    const [, start, end] = closureMatch;
                                    return (
                                      <Flex
                                        key={`${dayName}-closure-${segmentIndex}`}
                                        align="center"
                                        gap="0.5rem"
                                        flexWrap="wrap"
                                      >
                                        <Badge
                                          colorScheme="orange"
                                          variant="subtle"
                                          fontSize="0.625rem"
                                          textTransform="uppercase"
                                          px="0.35rem"
                                          py="0.125rem"
                                          borderRadius="0.25rem"
                                          flexShrink={0}
                                          minW="5.5rem"
                                          textAlign="center"
                                        >
                                          Lobby Closure
                                        </Badge>
                                        <Text
                                          color="orange.600"
                                          fontSize="0.8125rem"
                                          fontWeight={
                                            isCurrentDay ? '700' : '500'
                                          }
                                          lineHeight="1.35rem"
                                          whiteSpace="nowrap"
                                        >
                                          {ensureAmPmSpace(
                                            start
                                          )}{' '}
                                          –{' '}
                                          {ensureAmPmSpace(end)}
                                        </Text>
                                      </Flex>
                                    );
                                  }

                                  return (
                                    <Text
                                      key={`${dayName}-segment-${segmentIndex}`}
                                      color="text.secondary"
                                      fontSize="0.8125rem"
                                      fontWeight={
                                        isCurrentDay ? '700' : '400'
                                      }
                                      lineHeight="1.35rem"
                                      whiteSpace="nowrap"
                                    >
                                      {segment
                                        .replace(/\s+-\s+/g, ' – ')
                                        .replace(/\s*[-–—]\s*$/, '')}
                                    </Text>
                                  );
                                })}
                            </Stack>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
            {status === 'LOBBY CLOSURE APPROACHING' &&
              lobbyClosureInfo && (
                <Flex
                  border="2px solid"
                  borderColor="orange.500"
                  borderRadius="0.5rem"
                  p="0.5rem 0.625rem"
                  align="center"
                  gap="0.75rem"
                  mt="0.75rem"
                >
                  <img src={AlertIcon} alt="Warning Icon" />
                  <Flex gap="0.25rem">
                    <Text
                      fontSize="0.75rem"
                      color="text.primary"
                      fontWeight="400"
                      lineHeight={'1rem'}
                    >
                      Lobby closing soon
                    </Text>
                    <Text
                      fontWeight="700"
                      fontSize="0.75rem"
                      color="text.primary"
                      lineHeight={'1rem'}
                    >
                      {' '}
                      {lobbyClosureInfo.start} -{' '}
                      {lobbyClosureInfo.end}
                    </Text>
                  </Flex>
                </Flex>
              )}
            {status === 'LOBBY CLOSURE' && lobbyClosureInfo && (
              <Flex
                border="2px solid"
                borderColor="background.yellow500"
                borderRadius="0.5rem"
                p="0.5rem 0.625rem"
                align="center"
                gap="0.75rem"
                mt="0.75rem"
              >
                <img src={AlertIcon} alt="Warning Icon" />
                <Flex gap="0.25rem">
                  <Text
                    fontSize="0.75rem"
                    color="text.primary"
                    fontWeight="400"
                    lineHeight={'1rem'}
                  >
                    Lobby closed
                  </Text>
                  <Text
                    fontWeight={700}
                    fontSize="0.75rem"
                    color="text.primary"
                    lineHeight={'1rem'}
                  >
                    {' '}
                    {lobbyClosureInfo.start} - {lobbyClosureInfo.end}
                  </Text>
                </Flex>
              </Flex>
            )}
          </Stack>
        </Collapse>
      </Box>
    </Skeleton>
  );
};

export default PracticeStatusBox;
